import { type FC, type ReactElement, type ReactNode, useCallback } from "react";

import { Button, type Colors } from "@snapchat/snap-design-system";
import { type IconProps } from "@snapchat/snap-design-system-icons";

import { type Visibility } from "../../../gql-generated/types";
import { LensVisibilityCompact } from "../../../lens-visibility/components";
import { type LensDetailsVisibilityResult } from "../../../lens-visibility/hooks";
import { LensStatusWithDetails } from "../shared/LensStatusWithDetails";

import { LensStatusPanelContainer } from "./LensStatusPanel.styles";

export interface LensStatusPanelProps {
    title: string;
    tagLine?: string;
    color: Colors.ColorPalette;
    isColorInverted?: boolean;
    loading: boolean;
    icon?: (props: IconProps) => ReactElement;

    showCta: boolean;
    isCtaDisabled?: boolean;
    ctaText?: ReactNode;
    onAction?: () => void;

    visibility: Visibility;
    showVisibility?: boolean;
    isVisibilityDisabled?: boolean;
    lensDetailsVisibilityConfig: LensDetailsVisibilityResult;

    tooltipContent?: ReactNode;
    errorModal?: ReactNode;
    publishModal?: ReactNode;
    hasDistributionRestrictions?: boolean;
    isSpectaclesOnly?: boolean;
}

export const LensStatusPanel: FC<LensStatusPanelProps> = ({
    showCta,
    isCtaDisabled,
    onAction,
    ctaText,
    showVisibility,
    isVisibilityDisabled,
    visibility,
    tooltipContent,
    loading,
    errorModal,
    publishModal,
    lensDetailsVisibilityConfig,
    isSpectaclesOnly,
    ...lensStatusWithDetailsProps
}) => {
    const {
        changeVisibility,
        changeVisibilityLoading,
        lensVisibilityConfig: { disabledOptions, options },
    } = lensDetailsVisibilityConfig;

    const handleVisibilityChange = useCallback(
        (value: Visibility) => {
            void changeVisibility(value);
        },
        [changeVisibility],
    );
    return (
        <>
            <LensStatusPanelContainer>
                <LensStatusWithDetails
                    tooltipContent={tooltipContent}
                    loading={loading}
                    isSpectaclesOnly={isSpectaclesOnly}
                    {...lensStatusWithDetailsProps}
                />
                {showCta && (
                    <Button
                        type="primary"
                        isDisabled={isCtaDisabled}
                        onClick={onAction}
                        isPending={false}
                        data-testid="publish-button"
                    >
                        {ctaText}
                    </Button>
                )}
                {showVisibility && (
                    <LensVisibilityCompact
                        setVisibility={handleVisibilityChange}
                        loading={changeVisibilityLoading}
                        visibility={visibility}
                        options={options}
                        disabledOptions={disabledOptions}
                        disabled={isVisibilityDisabled}
                    />
                )}
            </LensStatusPanelContainer>

            {errorModal && errorModal}
            {publishModal && publishModal}
        </>
    );
};
